import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { useEffect, useMemo, useRef, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { usePreference } from '@/hooks/usePreference'
import {
  useGetPssLabelRecordQuery,
  useLazyGetPssLabelPrintPreviewQuery,
  type PssLabelRecord,
} from '@/store/api/qualityPssLabelApi'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

/**
 * PSS Label print dialog — single-label printer for the sample row selected in
 * the Sampling Data list (Access port: form `frmQualityPssLableSubForm`,
 * report `RpQualityPssLable_<copies>`). Merged here from the standalone
 * `Pr PSS Label` tab: pick copies → preview → print, scoped to one sample_no.
 *
 *  - Record source: `tlc_factory.view_factory_sampling_psslable2` by sample_no.
 *  - Copies (`cbCopies`): free numeric input 1–50, persisted per account
 *    (user_preferences scope=quality / page_filter, sub_key 'pss-label').
 */

interface PssCopiesPref {
  copies: number
}

// Fit-A4 preview overrides for the scaled iframe (memory: print fit A4).
const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 200mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
.pss-label { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
`.trim()

function buildPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="pss-label-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Access delivery_to Format ="mmm-yy" (e.g. Jul-26).
function fmtMmmYy(value: string | null): string {
  if (!value) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!m) return value
  return `${MONTHS[Number(m[2]) - 1]}-${m[1].slice(2)}`
}

function fmtQty(value: number | null): string {
  if (value == null) return '—'
  return Number.isInteger(value) ? value.toLocaleString('en-US') : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function LabelField({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <Typography sx={{ width: { xs: 92, sm: 110 }, flexShrink: 0, fontWeight: 700, fontSize: '0.85rem' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
  )
}

interface SamplingPssLabelDialogProps {
  open: boolean
  sampleNo: string | null
  onClose: () => void
}

export default function SamplingPssLabelDialog({ open, sampleNo, onClose }: SamplingPssLabelDialogProps) {
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewing, setPreviewing] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  // Copies persisted per account (user_preferences). Local state drives the
  // input; debounced patch writes the delta.
  const copiesPref = usePreference<PssCopiesPref>('quality', 'page_filter', 'pss-label')
  const [copies, setCopies] = useState('1')
  const copiesHydrated = useRef(false)

  const { data: record, isFetching: recordLoading } = useGetPssLabelRecordQuery(sampleNo ?? '', {
    skip: !sampleNo || !open,
  })
  const [triggerPreview] = useLazyGetPssLabelPrintPreviewQuery()

  // Hydrate copies once from the persisted preference.
  useEffect(() => {
    if (copiesPref.isLoading || copiesHydrated.current) return
    copiesHydrated.current = true
    setCopies(String(copiesPref.value.copies ?? 1))
  }, [copiesPref])

  // Persist copies (debounced) once a valid number is entered.
  useEffect(() => {
    if (!copiesHydrated.current) return
    const n = Math.floor(Number(copies))
    if (!Number.isFinite(n) || n < 1 || n > 50 || n === copiesPref.value.copies) return
    const timer = setTimeout(() => {
      void copiesPref.patch({ copies: n }).catch(() => undefined)
    }, 450)
    return () => clearTimeout(timer)
  }, [copies, copiesPref])

  const rows = useMemo<Array<{ label: string; value: string }>>(() => {
    const r: PssLabelRecord | undefined = record
    // All values come from the DB view (Access RpQualityPssLable_1 binds every
    // field to a column) — no hard-coded labels.
    return [
      { label: 'Sample Type:', value: r?.sample_type ?? '' },
      { label: 'Contract:', value: r?.txtcontract ?? '' },
      { label: 'Client Lot ID:', value: r?.client_lot_id ?? '' },
      { label: 'Consignee:', value: r?.consignee ?? '' },
      { label: 'Grade:', value: r?.grade ?? '' },
      { label: 'Quantity:', value: r ? `${fmtQty(r.quantity_pr)} ${r.unit_pr ?? ''}`.trim() : '' },
      { label: 'Shipment:', value: fmtMmmYy(r?.delivery_to ?? null) },
      { label: 'Terms:', value: r?.pss_terms ?? '' },
      { label: 'Shipper:', value: r?.shipper_pr ?? '' },
      { label: 'Contact:', value: r?.email_pr ?? '' },
    ]
  }, [record])

  const copiesNum = Math.floor(Number(copies))
  const copiesValid = Number.isFinite(copiesNum) && copiesNum >= 1 && copiesNum <= 50

  const handlePrint = async () => {
    if (!sampleNo) {
      setToastMessage('Select a sample number first.')
      return
    }
    if (!copiesValid) {
      setToastMessage('Enter a number of copies between 1 and 50.')
      return
    }
    setPreviewing(true)
    try {
      const result = await triggerPreview({ sampleNo, copies: copiesNum }).unwrap()
      setPreviewHtml(buildPreviewHtml(result.html))
      setToastMessage(result.message ?? 'Print preview generated.')
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      setToastMessage(e?.status === 422 ? e.data?.detail || 'Cannot print this sample.' : 'Failed to generate print preview.')
    } finally {
      setPreviewing(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span" title={`PSS Label (${sampleNo ?? ''})`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}>
            PSS Label{sampleNo ? ` (${sampleNo})` : ''}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ py: 0.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                label="Copies"
                type="number"
                size="small"
                value={copies}
                onChange={(e) => setCopies(e.target.value)}
                error={Boolean(copies) && !copiesValid}
                inputProps={{ min: 1, max: 50, step: 1 }}
                sx={{ width: 96, flexShrink: 0 }}
              />
              <Button
                variant="contained"
                startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
                onClick={handlePrint}
                disabled={!sampleNo || !copiesValid || previewing}
                title="Print preview"
                sx={{ whiteSpace: 'nowrap' }}
              >
                Print<Box component="span" sx={HIDE_ON_MOBILE_INLINE}>&nbsp;preview</Box>
              </Button>
            </Stack>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                position: 'relative',
                minHeight: 220,
              }}
            >
              {recordLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)' }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              <Stack spacing={0.75}>
                {rows.map((row) => (
                  <LabelField key={row.label} label={row.label} value={row.value} />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {previewing && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.35)' }}>
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog open={Boolean(previewHtml)} onClose={() => setPreviewHtml('')} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span" title={`PSS Label (${sampleNo ?? ''})`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}>
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            PSS Label{sampleNo ? ` (${sampleNo})` : ''}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setPreviewHtml('')} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(sampleNo ?? 'pss')}_PSS_Label.pdf`
                void printAndUploadReport({
                  iframeTitle: 'PSS Label Print Preview',
                  reportName: 'RpQualityPssLable',
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title="PSS Label Print Preview"
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}
